import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateTimeService } from '@examdojo/core/date-time';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { StreakService } from '@examdojo/streak';
import { UserSettingsQuery } from '@examdojo/user-settings';
import { defer, distinctUntilChanged, EMPTY, exhaustMap, expand, filter, finalize, fromEvent, map, merge, switchMap, tap, timer } from 'rxjs';
import { UserMarksService } from '../user/mark/user-marks.service';

@Injectable()
export class RefreshTimeSensitiveUserDataOnDayChangeEffectService {
  constructor(
    private readonly userSettingsQuery: UserSettingsQuery,
    private readonly userMarksService: UserMarksService,
    private readonly streakService: StreakService,
    private readonly dateTimeService: DateTimeService,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.userSettingsQuery.timezone$
      .pipe(
        map((timezone) => timezone ?? 'UTC'),
        distinctUntilChanged(),
        switchMap((timezone) => this.refreshOnUserDayChange(timezone)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private refreshOnUserDayChange(timezone: string) {
    let lastObservedUserDay = this.getCurrentUserDay(timezone);

    return merge(this.getUserMidnightRollover$(timezone), this.getResumeSignals$()).pipe(
      filter(() => this.getCurrentUserDay(timezone) !== lastObservedUserDay),
      exhaustMap(() => {
        const dayToRefresh = this.getCurrentUserDay(timezone);
        let didRefreshSucceed = false;

        return merge(
          this.userMarksService.fetchLastWeekUserMarks().pipe(this.errorHandlerService.catchHttpErrors(() => EMPTY)),
          this.userMarksService.getUserSpentPointsAndLimits().pipe(this.errorHandlerService.catchHttpErrors(() => EMPTY)),
          this.streakService.fetchStreaks().pipe(this.errorHandlerService.catchHttpErrors(() => EMPTY)),
        ).pipe(
          tap(() => {
            didRefreshSucceed = true;
          }),
          finalize(() => {
            if (didRefreshSucceed) {
              lastObservedUserDay = dayToRefresh;
            }
          }),
        );
      }),
    );
  }

  private getResumeSignals$() {
    const defaultView = this.document.defaultView;

    if (!defaultView) {
      return EMPTY;
    }

    return merge(
      fromEvent(defaultView, 'focus'),
      fromEvent(defaultView, 'online'),
      fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.visibilityState === 'visible')),
    );
  }

  private getUserMidnightRollover$(timezone: string) {
    return defer(() => timer(this.getMillisecondsUntilNextUserMidnight(timezone))).pipe(
      expand(() => timer(this.getMillisecondsUntilNextUserMidnight(timezone))),
    );
  }

  private getCurrentUserDay(timezone: string): string {
    return this.dateTimeService.formatToShortIsoDate(new Date(), { timezone });
  }

  private getMillisecondsUntilNextUserMidnight(timezone: string): number {
    const today = this.getCurrentUserDay(timezone);
    const [year, month, day] = today.split('-').map(Number);
    const todayMidnight = this.dateTimeService.createDateTimeInTimezone(year, month - 1, day, 0, 0, timezone);
    const nextMidnight = this.dateTimeService.add(todayMidnight, { days: 1 });

    return Math.max(this.dateTimeService.diff(new Date(), nextMidnight, 'milliseconds') + 1000, 1000);
  }
}
